import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";

import { SidePageWrapper } from "../../../layout/sidePageWrapper/SidePageWrapper";
import { QuickPayPayment } from "../../../pdf/QuickPayPayment";

import { useRootDispatch, useRootSelector } from "store/hooks";
import { useAssets } from "../../../../hooks/useAssets";
import { resetAccounts } from "store/account/account-slice";
import { resetInfo as paymentReset } from "store/payments/payments-slice";

import classes from "./Confirmation.module.scss";
import "../../../pdf/Print.scss";
import { QuickPayCompletionLinkType, QuickPayType } from "store/config";
import { useAuth0 } from "@auth0/auth0-react";
import { activeAccountTypeConfig } from "store/account/account-selector";

export const Confirmation = () => {
  const { loginWithRedirect } = useAuth0<{
    email_verified: boolean;
  }>();
  const navigate = useNavigate();
  const dispatch = useRootDispatch();
  const { createAssetUrl } = useAssets();
  const paymentData = useRootSelector((state) => state.payments.paymentData);
  const { client, paymentCenter } = useRootSelector((state) => state.config);
  const currentAccountTypeConfig = useRootSelector(activeAccountTypeConfig);

  const newPaymentHandler = () => {
    dispatch(resetAccounts());
    navigate(`/${client.customerKey}/quick-info`);
  };
  const { t } = useTranslation(["payment_final", "payment", "common"]);

  const quickPayType = paymentCenter.quickPayments.quickPayType;

  useEffect(() => {
    if (!paymentData || !paymentData.paymentResponse) {
      navigate(`/${client.customerKey}/quick-review`);
    }
  }, [paymentData, navigate, client.customerKey]);

  useEffect(() => {
    return () => {
      sessionStorage.removeItem("ft-Token");
      dispatch(paymentReset());
      dispatch(resetAccounts());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => (componentRef.current ? componentRef.current : null),
  });

  const postCompletionHandler = () => {
    switch (
      currentAccountTypeConfig.quickPayments.completionPageLinkFunctionality
    ) {
      case QuickPayCompletionLinkType.Close: {
        window.opener = null;
        window.open("", "_self");
        window.close();
        break;
      }
      case QuickPayCompletionLinkType.Navigate:
        if (
          currentAccountTypeConfig.quickPayments.postCompletionLinkUrl != null
        ) {
          window.location.href =
            currentAccountTypeConfig.quickPayments.postCompletionLinkUrl;
        }
        break;
      case QuickPayCompletionLinkType.NavigatePost:
        //TODO
        break;
      default:
        break;
    }
  };

  const createAccount = () => {
    let enterActionId: number | null = null;
    if (!!(window as any).dtrum) {
      enterActionId = (window as any).dtrum.enterAction(
        "create-account-quick-pay",
      ) as number;
    }
    loginWithRedirect({
      authorizationParams: { screen_hint: "signup" },
    });
    if (!!enterActionId && !!(window as any).dtrum) {
      console.log("leaving action ", enterActionId);
      (window as any).dtrum.leaveAction(enterActionId);
    }
  };

  return (
    <>
      <SidePageWrapper hideDisclaime={true}>
        <div className={classes.Container}>
          <div data-testid="congratulations-div" className={classes.Main}>
            <p data-testid="congratulations" className={classes.Heading}>
              {t("payment_final:congratulations")}
            </p>
            <p className={classes.Heading}>
              {t("payment_final:payment_complete")}
            </p>
            <div className={classes.Detail}>
              <p>
                On{" "}
                {t("common:date_time_format", {
                  value: paymentData?.paymentResponse?.paymentDate,
                })}
              </p>
            </div>
            <div className={classes.Confirmation_detail}>
              <div className={classes.Items}>
                <p className={classes.Items_heading}>
                  {t("payment:confirmationNumber")}
                </p>
                <p data-testid="quick-pay-confirmation-confirmation-number">
                  {paymentData?.paymentResponse?.id}
                </p>
              </div>
              <div className={classes.Items}>
                <p className={classes.Items_heading}>
                  {t("payment_final:payment_amount")}
                </p>
                <p data-testid="quick-pay-confirmation-amount-paid">
                  {t("common:currency_format", {
                    value: +(paymentData?.paymentAmount ?? 0),
                  })}
                </p>
              </div>
              {paymentData?.feeTotal != null && paymentData.feeTotal > 0 ? (
                <>
                  <div className={classes.Items}>
                    <p className={classes.Items_heading}>
                      {t("payment_final:convenience_fee_label")}
                    </p>
                    <p>
                      {t("common:currency_format", {
                        value: paymentData.feeTotal,
                      })}
                    </p>
                  </div>
                  <div className={classes.Items}>
                    <p className={classes.Items_heading}>
                      {t("payment_final:total_payment")}
                    </p>
                    <p>
                      {t("common:currency_format", {
                        value:
                          paymentData.feeTotal +
                          +(paymentData.paymentAmount ?? 0),
                      })}
                    </p>
                  </div>
                </>
              ) : null}
              <ft-icon
                onClick={handlePrint}
                aria-label={t("common:download")}
                title={t("common:download")}
              >
                file_download
              </ft-icon>
            </div>
            <div className={classes.Buttons}>
              {currentAccountTypeConfig.quickPayments
                .completionPageLinkFunctionality > 0 ? (
                <>
                  <p className={classes.Close_message}>
                    {t("payment_final:close-page")}
                  </p>
                  <ft-button
                    onClick={postCompletionHandler}
                    class={classes.ButtonSpacing}
                    label={t("common:exit")}
                    unelevated
                  ></ft-button>
                </>
              ) : (
                <ft-button
                  onClick={newPaymentHandler}
                  class={classes.ButtonSpacing}
                  unelevated
                  label={t("payment_final:new_payment")}
                ></ft-button>
              )}
              {client.userRegistrationOption === 1 &&
              quickPayType !== QuickPayType.NonValidated ? (
                <ft-button
                  data-testid="login-page-signup"
                  class="outline-button"
                  label={t("app:create_account_label")}
                  onClick={createAccount}
                  outlined
                ></ft-button>
              ) : (
                <></>
              )}
            </div>
          </div>
          <div className={classes.Image_container}>
            <img
              alt="confirmation"
              src={createAssetUrl("successful-action")}
            ></img>
          </div>
        </div>
      </SidePageWrapper>
      <div className={classes.PrintCss}>
        <QuickPayPayment
          paymentData={paymentData ? paymentData : undefined}
          ref={componentRef}
        />
      </div>
    </>
  );
};
